import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { message, Space, Typography, Spin, Button, Pagination, Card } from 'antd';
// import api, { monthlyMetricService } from '../../services/api'; // Artık kullanılmıyor
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.css';
import '../../styles/handsontable.css';

// Bileşenler
import ControlPanel from './components/ControlPanel';
import BrandCard from './components/BrandCard';
import MetricSelector from './components/MetricSelector';
import NewSummaryTableModal from './components/NewSummaryTableModal';

// Hook'lar
import useBrandsData from './hooks/useBrandsData';
import useMetricsData from './hooks/useMetricsData';

const { Text } = Typography;

// Türkçe dil ayarı
dayjs.locale('tr');
registerAllModules();

// Cache süresi (milisaniye cinsinden)
const CACHE_DURATION = 10 * 60 * 1000; // 5 dakika

// Marka kartını memo ile sarmalayacak bir bileşen
const MemoizedBrandCard = React.memo(BrandCard, (prevProps, nextProps) => {
  // Sadece gerekli props değiştiğinde yeniden render et
  return (
    prevProps.brand.id === nextProps.brand.id &&
    prevProps.loading === nextProps.loading &&
    prevProps.index === nextProps.index &&
    // allMetricsData nesnesinde ilgili markaya ait veri aynıysa render etme
    (prevProps.allMetricsData[prevProps.brand.id] === nextProps.allMetricsData[nextProps.brand.id]) &&
    prevProps.allCampaigns === nextProps.allCampaigns
  );
});

const AllBrandsMetrics = ({ isEmbedded = false, tokenData = null }) => {
  // Default metrikler
  const defaultMetricDescriptions = useMemo(() => [
    'TOPLAM CİRO',
    'CİRO HEDEF SAPMASI', 
    'AY SONU TAHMİNİ CİRO',
    'ÖNCEKİ AYA GÖRE TAHMİNİ DEĞİŞİM',
    'GÜNLÜK ORTALAMA CİRO',
    'GÜNLÜK GEREKEN CİRO',
    'ROI',
    'HEDEF ROI'
  ], []);

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15; // Sayfa başına gösterilecek marka sayısı
  const [visibleBrands, setVisibleBrands] = useState([]); // Sadece görünen markalar
  
  // Cache state'leri
  const [cachedBrandData, setCachedBrandData] = useState({}); // Marka ID'sine göre veri önbelleği
  const [cacheTimestamps, setCacheTimestamps] = useState({}); // Marka ID'sine göre önbellek zaman damgaları
  
  // Metrik Seçici ve Özet Tablo için state'ler
  const [isMetricSelectorVisible, setIsMetricSelectorVisible] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedMetricDescriptions, setSelectedMetricDescriptions] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  // Modal state'leri artık NewSummaryTableModal içinde yönetiliyor
  
  // Pasif markalar için state - Artık her zaman true, otomatik olarak o ayda verisi olan pasif markalar gösterilecek
  const includeInactive = true;
  
  // Hook'ları kullan - önce useMetricsData'dan selectedMonth'u al
  const {
    selectedMonth,
    isPickerOpen,
    setIsPickerOpen,
    allMetricsData,
    metrics,
    gridData,
    fetchedBrandIds,
    fetchDataForBrands,
    handleMonthChange,
    handleRecalculateAllBrands,
    handleRecalculateForBrand,
    allCampaigns,
    fetchAllCampaigns
  } = useMetricsData();
  
  const {
    allBrands,
    brands,
    loading,
    setLoading,
    hasMore,
    loadMoreBrands,
    searchValue,
    setSearchValue,
    filteredBrands,
    isFiltering,
    searchInputRef,
    loadBrandsPage,
    
    // Tüm advisor listeleri
    advisors,
    advisorsLoading,
    brandGrowthAdvisors,
    brandAssistantAdvisors,
    brandMetaAdvisors,
    brandGoogleAdvisors,
    brandSeoAdvisors,
    
    // Tüm advisor filtreleri
    selectedAdvisor,
    selectedGrowth,
    selectedAssistant,
    selectedMeta,
    selectedGoogle,
    selectedSeo,
    
    // Tüm advisor handler'lar
    handleAdvisorChange,
    handleGrowthChange,
    handleAssistantChange,
    handleMetaChange,
    handleGoogleChange,
    handleSeoChange,
    
    // Status filtresi
    selectedStatus,
    handleStatusChange
  } = useBrandsData(pageSize, tokenData?.brandIds, includeInactive, selectedMonth); // İlk sayfa için pageSize kadar marka yükle
  
  // ControlPanel için wrapper fonksiyonlar - sonsuz döngü sorununu çözmek için optimize ediyoruz
  const handlePickerOpenChange = useCallback((open) => {
    // setIsPickerOpen'ı doğrudan çağır, karşılaştırma yapmaya gerek yok
    if (typeof setIsPickerOpen === 'function') {
      setIsPickerOpen(open);
    }
  }, [setIsPickerOpen]);
  
  // İlk render'da veri yükle ve localStorage'dan metrik bilgilerini çek
  useEffect(() => {
    // Sayfa verilerini yükle
    loadPageData(1);
    fetchAllCampaigns(); // İlk yüklemede kampanya verilerini getir
    
    // localStorage'dan metrik seçimlerini yükle
    try {
      const savedMetricDescriptions = localStorage.getItem('selectedMetricDescriptions');
      const savedMetrics = localStorage.getItem('selectedMetrics');
      
      // Önce description'ları kontrol et (daha yeni yaklaşım)
      if (savedMetricDescriptions) {
        try {
          const descriptionsArray = JSON.parse(savedMetricDescriptions);
          if (Array.isArray(descriptionsArray) && descriptionsArray.length > 0) {
            setSelectedMetricDescriptions(descriptionsArray);
            
            // ID'leri de yükle
            if (savedMetrics) {
              const metricsArray = JSON.parse(savedMetrics);
              if (Array.isArray(metricsArray)) {
                setSelectedMetrics(metricsArray);
              }
            }
            
            // Özet tabloyu göster
            setShowSummaryModal(false);
          }
        } catch (e) {
          console.error('Description parsing hatası:', e);
        }
      }
      // Eski yöntem - sadece metrik ID'ler var
      else if (savedMetrics) {
        try {
          const metricsArray = JSON.parse(savedMetrics);
          if (Array.isArray(metricsArray) && metricsArray.length > 0) {
            setSelectedMetrics(metricsArray);
            setShowSummaryModal(false);
          }
        } catch (e) {
          console.error('Metrics parsing hatası:', e);
        }
      }
    } catch (error) {
      console.error('localStorage erişim hatası:', error);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Önbellekte markalar var mı ve önbellek süresi dolmuş mu kontrol et
  const checkCache = useCallback((brandIds) => {
    const now = Date.now();
    const expiredBrandIds = [];
    const validBrandIds = [];
    
    brandIds.forEach(brandId => {
      const timestamp = cacheTimestamps[brandId];
      
      // Önbellekte yoksa veya süresi dolduysa
      if (!timestamp || (now - timestamp) > CACHE_DURATION) {
        expiredBrandIds.push(brandId);
      } else {
        validBrandIds.push(brandId);
      }
    });
    
    return { expiredBrandIds, validBrandIds };
  }, [cacheTimestamps]);
  
  // Markaları önbelleğe ekle
  const cacheBrandData = useCallback((brandsData) => {
    const now = Date.now();
    
    setCachedBrandData(prevCache => {
      const newCache = { ...prevCache };
      
      brandsData.forEach(brand => {
        newCache[brand.id] = brand;
      });
      
      return newCache;
    });
    
    setCacheTimestamps(prevTimestamps => {
      const newTimestamps = { ...prevTimestamps };
      
      brandsData.forEach(brand => {
        newTimestamps[brand.id] = now;
      });
      
      return newTimestamps;
    });
  }, []); // Boş bağımlılık dizisi kullanarak fonksiyonu stabil tutuyoruz
  
  // API'dan gelen verileri önbelleğe ekle
  useEffect(() => {
    // Sadece allMetricsData değiştiğinde ve veri varsa cache'e ekle
    if (allMetricsData && Object.keys(allMetricsData).length > 0) {
      const brandsWithData = Object.keys(allMetricsData).map(brandId => ({
        id: parseInt(brandId, 10),
        data: allMetricsData[brandId]
      }));
      
      if (brandsWithData.length > 0) {
        cacheBrandData(brandsWithData);
      }
    }
  }, [allMetricsData, cacheBrandData]);
  
  // loadPageData fonksiyonunu düzenleyelim ve bağımlılıkları azaltalım
  const loadPageData = useCallback((page) => {
    if (!page || page < 1) return; // Geçersiz sayfa numaraları için işlem yapma
    
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    
    if (isFiltering) {
      // Filtreleme varsa, filtrelenmiş markalardan ilgili sayfayı göster
      const pageFilteredBrands = filteredBrands.slice(start, end);
      setVisibleBrands(pageFilteredBrands);
      
      // Cache kontrolü yap
      const brandIds = pageFilteredBrands.map(brand => brand.id);
      const { expiredBrandIds } = checkCache(brandIds);
      
      // Sadece önbellekte olmayan ya da süresi dolan markaların verilerini getir
      if (expiredBrandIds.length > 0) {
        const brandsToLoad = pageFilteredBrands.filter(brand => 
          expiredBrandIds.includes(brand.id)
        );
        
        if (brandsToLoad.length > 0) {
          fetchDataForBrands(brandsToLoad, setLoading);
        }
      }
    } else {
      // Filtreleme yoksa sadece ilgili sayfanın markalarını yükle
      if (start >= brands.length && hasMore) {
        // Eğer istenen sayfa daha yüklenmemişse, yükle
        loadMoreBrands(start, pageSize).then(loadedBrands => {
          if (loadedBrands && loadedBrands.length > 0) {
            setVisibleBrands(loadedBrands);
            
            // Cache kontrolü yap
            const brandIds = loadedBrands.map(brand => brand.id);
            const { expiredBrandIds } = checkCache(brandIds);
            
            // Sadece önbellekte olmayan ya da süresi dolan markaların verilerini getir
            if (expiredBrandIds.length > 0) {
              const brandsToLoad = loadedBrands.filter(brand => 
                expiredBrandIds.includes(brand.id)
              );
              
              if (brandsToLoad.length > 0) {
                fetchDataForBrands(brandsToLoad, setLoading);
              }
            }
          }
        });
      } else {
        // İlgili sayfa zaten yüklenmişse, göster
        const pageBrands = brands.slice(start, end);
        setVisibleBrands(pageBrands);
        
        // Cache kontrolü yap
        const brandIds = pageBrands.map(brand => brand.id);
        const { expiredBrandIds } = checkCache(brandIds);
        
        // Sadece önbellekte olmayan ya da süresi dolan markaların verilerini getir
        if (expiredBrandIds.length > 0) {
          const brandsToLoad = pageBrands.filter(brand => 
            expiredBrandIds.includes(brand.id)
          );
          
          if (brandsToLoad.length > 0) {
            fetchDataForBrands(brandsToLoad, setLoading);
          }
        }
      }
    }
  }, [
    brands, 
    filteredBrands, 
    isFiltering, 
    fetchDataForBrands, 
    setLoading, 
    hasMore, 
    loadMoreBrands, 
    pageSize, 
    checkCache
  ]);
  
  // Sayfa değiştiğinde yeni verileri yükle
  useEffect(() => {
    // Sadece geçerli currentPage değerinde veri yükle
    if (currentPage > 0) {
      // Burada direkt loadPageData yerine useCallback ile sarmalanmış stabil bir fonksiyon çağrılmalı
      const pageToLoad = currentPage;
      loadPageData(pageToLoad);
    }
  }, [currentPage, loadPageData]);
  
  // İlk yüklemede ve arama değiştiğinde sayfayı sıfırla
  useEffect(() => {
    // Sadece searchValue değiştiğinde sayfayı sıfırla
    if (searchValue !== undefined && searchValue !== "") {
      setCurrentPage(1);
    }
  }, [searchValue]);
  
  // Daha fazla marka yükle (sayfa değiştiğinde)
  const handlePageChange = useCallback((page) => {
    // Aynı sayfaya tıklandıysa işlem yapma
    if (page === currentPage) return;
    
    // Sayfa değiştiğinde doğrudan sayfa numarasını değiştir
    setCurrentPage(page);
    
    // Sayfayı en üste kaydır
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentPage]);
  
  // Ay değişimi için wrapper fonksiyon - bağımlılıkları azaltıyoruz
  const handleMonthChangeWrapper = useCallback((date) => {
    // Ay değiştiğinde cache'i temizle
    setCachedBrandData({});
    setCacheTimestamps({});
    
    // visibleBrands ve setLoading'i sabit referans değerler olarak geçirelim
    handleMonthChange(date, visibleBrands, setLoading);
    
    // Sayfayı başa al
    setCurrentPage(1);
  }, [handleMonthChange, visibleBrands, setLoading]);

  // Metrik seçimleri değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (selectedMetrics.length > 0) {
      localStorage.setItem('selectedMetrics', JSON.stringify(selectedMetrics));
      localStorage.setItem('selectedMetricDescriptions', JSON.stringify(selectedMetricDescriptions));
    }
  }, [selectedMetrics, selectedMetricDescriptions]);
  
  // Yeniden hesaplama için wrapper fonksiyonlar - bağımlılıkları azaltıyoruz
  const handleRecalculateAllBrandsWrapper = useCallback(() => {
    // Yeniden hesaplama yapıldığında cache'i temizle
    setCachedBrandData({});
    setCacheTimestamps({});
    
    handleRecalculateAllBrands(visibleBrands, setLoading);
  }, [handleRecalculateAllBrands, visibleBrands, setLoading]);
  
  // Marka için yeniden hesaplama
  const handleRecalculateForBrandWrapper = useCallback((brandId, brandName) => {
    // Bu markaya ait cache'i temizle
    const newCache = { ...cachedBrandData };
    const newTimestamps = { ...cacheTimestamps };
    
    if (newCache[brandId]) {
      delete newCache[brandId];
    }
    
    if (newTimestamps[brandId]) {
      delete newTimestamps[brandId];
    }
    
    setCachedBrandData(newCache);
    setCacheTimestamps(newTimestamps);
    
    handleRecalculateForBrand(brandId, brandName, visibleBrands, setLoading);
  }, [handleRecalculateForBrand, visibleBrands, setLoading, cachedBrandData, cacheTimestamps]);
  
  // Toplam sayfa sayısı - useMemo ile hesaplayalım
  const totalBrands = useMemo(() => 
    isFiltering ? filteredBrands.length : allBrands.length,
  [isFiltering, filteredBrands, allBrands]);
  
  // Önbellek durumunu göster
  const cacheInfo = useMemo(() => {
    const cachedBrands = Object.keys(cacheTimestamps).length;
    const now = Date.now();
    const activeCachedBrands = Object.entries(cacheTimestamps).filter(
      ([_, timestamp]) => (now - timestamp) <= CACHE_DURATION
    ).length;
    
    return { cachedBrands, activeCachedBrands };
  }, [cacheTimestamps]);
  
  // Önbelleği temizle
  const clearCache = useCallback(() => {
    setCachedBrandData({});
    setCacheTimestamps({});
    message.success('Önbellek başarıyla temizlendi');
    
    // Mevcut sayfanın verilerini yeniden yükle
    loadPageData(currentPage);
  }, [currentPage, loadPageData]);
  
  // Sayfalama bileşeni - bağımlılıkları doğru ekleyelim
  const renderPagination = useMemo(() => (
    <div style={{ textAlign: 'center', margin: '20px 0' }}>
      <Pagination
        current={currentPage}
        onChange={handlePageChange}
        total={totalBrands}
        pageSize={pageSize}
        showSizeChanger={false}
        showTotal={(total) => `Toplam ${total} marka`}
      />
    </div>
  ), [currentPage, handlePageChange, totalBrands, pageSize]);
  
  // Görünen markaları memo ile optimize et
  const memoizedVisibleBrands = useMemo(() => visibleBrands, [visibleBrands]);
  
  // Markalar listesini memo ile optimize et
  const brandsList = useMemo(() => {
    if (loading && memoizedVisibleBrands.length === 0) {
      return (
        <div className="text-center py-8 sm:py-12">
          <Spin size="large" />
          <p className="mt-2 text-sm sm:text-base text-gray-600">Markalar yükleniyor...</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-3 sm:space-y-4">
        {memoizedVisibleBrands.map((brand, index) => {
          // Güvenlik kontrolü
          if (!brand || typeof brand !== 'object') {
            return (
              <Card key={`empty-${index}`} className="my-2 p-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Geçersiz marka verisi</p>
                </div>
              </Card>
            );
          }
          
          return (
            <MemoizedBrandCard
              key={`${brand.id}-${index}`}
              brand={brand}
              index={index}
              allMetricsData={allMetricsData}
              handleRecalculateForBrand={handleRecalculateForBrandWrapper}
              loading={loading}
              metrics={metrics}
              gridData={gridData}
              selectedMonth={selectedMonth}
              allCampaigns={allCampaigns}
              fetchAllCampaigns={fetchAllCampaigns}
              isEmbedded={isEmbedded}
            />
          );
        })}
      </div>
    );
  }, [
    memoizedVisibleBrands, 
    loading, 
    allMetricsData, 
    handleRecalculateForBrandWrapper, 
    metrics, 
    gridData,
    selectedMonth,
    allCampaigns,
    fetchAllCampaigns
  ]);
  
  // Sayfa içerik bileşenini memo ile optimize et
  const pageContent = useMemo(() => {
    if (isFiltering && filteredBrands.length === 0) {
      return (
        <div className="text-center py-8 sm:py-12">
          <p className="text-sm sm:text-base text-gray-600">"{searchValue}" aramasına uygun marka bulunamadı.</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="space-y-3 sm:space-y-4">
          {brandsList}
        </div>
        
        {/* Alt sayfalama bileşeni */}
        <div className="flex justify-center">
          {renderPagination}
        </div>
        
        {loading && (
          <div className="text-center py-4 sm:py-6">
            <Spin />
            <p className="mt-2 text-sm sm:text-base text-gray-600">Markalar yükleniyor...</p>
          </div>
        )}
        
        {!loading && (
          <div className="text-center py-2 sm:py-4">
            <p className="text-xs sm:text-sm text-gray-500">
              {isFiltering 
                ? `"${searchValue}" için ${filteredBrands.length} marka bulundu.` 
                : `Sayfa ${currentPage}, Toplam ${totalBrands} marka`}
            </p>
          </div>
        )}
      </div>
    );
  }, [
    isFiltering, 
    filteredBrands.length, 
    searchValue, 
    brandsList, 
    renderPagination, 
    loading, 
    currentPage, 
    totalBrands
  ]);
  
  // Metrik seçici popup'ı göster/gizle
  const toggleMetricSelector = () => {
    setIsMetricSelectorVisible(prev => !prev);
  };
  
  // Özet tabloyu modal olarak göster
  const showSummaryTableModal = () => {
    if (selectedMetrics.length > 0) {
      setShowSummaryModal(true);
    } else {
      setIsMetricSelectorVisible(true);
    }
  };
  
  // Modal için kullanılacak markalar - filtre durumuna göre
  const brandsForModal = useMemo(() => {
    if (isFiltering) {
      return filteredBrands; // Filtre aktifse filtrelenmiş markalar
    } else {
      return allBrands; // Filtre yoksa tüm markalar
    }
  }, [isFiltering, filteredBrands, allBrands]);
  
  
  // Seçilen metrikleri kaydet
  const handleMetricsSave = (metrics, descriptions) => {
    // Gelen değerleri doğrula
    const metricIds = Array.isArray(metrics) ? metrics : [];
    const metricDescriptions = Array.isArray(descriptions) ? descriptions : [];
    
    // State'leri güncelle
    setSelectedMetrics(metricIds);
    setSelectedMetricDescriptions(metricDescriptions);
    
    // localStorage'a kaydet veya temizle
    try {
      if (metricIds.length > 0) {
        localStorage.setItem('selectedMetrics', JSON.stringify(metricIds));
        localStorage.setItem('selectedMetricDescriptions', JSON.stringify(metricDescriptions));
      } else {
        // Metrik seçimi temizlendiyse localStorage'dan kaldır
        localStorage.removeItem('selectedMetrics');
        localStorage.removeItem('selectedMetricDescriptions');
      }
    } catch (error) {
      console.error('localStorage kaydetme hatası:', error);
    }
    
    // Popup'ı kapat
    setIsMetricSelectorVisible(false);
    
    // Eğer metrik seçildiyse özet tabloyu modal olarak göster
    if (metricIds.length > 0) {
      setShowSummaryModal(true);
    } else {
      setShowSummaryModal(false);
    }
  };

  // Özet modal'ı kapat
  const handleCloseSummaryModal = useCallback(() => {
    setShowSummaryModal(false);
  }, []);

  // Modal'daki metrik değişikliklerini işle
  const handleModalMetricsChange = useCallback((metrics, descriptions) => {
    setSelectedMetrics(metrics);
    setSelectedMetricDescriptions(descriptions);
    
    // localStorage'a kaydet
    try {
      if (metrics.length > 0) {
        localStorage.setItem('selectedMetrics', JSON.stringify(metrics));
        localStorage.setItem('selectedMetricDescriptions', JSON.stringify(descriptions));
      } else {
        localStorage.removeItem('selectedMetrics');
        localStorage.removeItem('selectedMetricDescriptions');
      }
    } catch (error) {
      console.error('localStorage kaydetme hatası:', error);
    }
  }, []);
  
  // visibleBrandsForSummary artık kullanılmıyor - brandsForModal kullanıyoruz

  return (
    <div className="px-2 sm:px-4 lg:px-6 py-2 sm:py-4">
      <div className="space-y-3 sm:space-y-4 lg:space-y-6">
        <ControlPanel
          selectedMonth={selectedMonth}
          searchValue={searchValue}
          loading={loading}
          searchInputRef={searchInputRef}
          handleMonthChange={handleMonthChangeWrapper}
          handleRecalculateAllBrands={handleRecalculateAllBrandsWrapper}
          setSearchValue={setSearchValue}
          setIsPickerOpen={handlePickerOpenChange}
          isPickerOpen={isPickerOpen}
          isEmbedded={isEmbedded}
          isAllBrandsToken={tokenData?.allBrands}
          advisors={advisors}
          advisorsLoading={advisorsLoading}
          selectedAdvisor={selectedAdvisor}
          handleAdvisorChange={handleAdvisorChange}
          
          // Yeni advisor filtreleri
          brandGrowthAdvisors={brandGrowthAdvisors}
          brandAssistantAdvisors={brandAssistantAdvisors}
          brandMetaAdvisors={brandMetaAdvisors}
          brandGoogleAdvisors={brandGoogleAdvisors}
          brandSeoAdvisors={brandSeoAdvisors}
          selectedGrowth={selectedGrowth}
          selectedAssistant={selectedAssistant}
          selectedMeta={selectedMeta}
          selectedGoogle={selectedGoogle}
          selectedSeo={selectedSeo}
          handleGrowthChange={handleGrowthChange}
          handleAssistantChange={handleAssistantChange}
          handleMetaChange={handleMetaChange}
          handleGoogleChange={handleGoogleChange}
          handleSeoChange={handleSeoChange}
          
          onMetricSelectorClick={showSummaryTableModal}
          selectedStatus={selectedStatus}
          handleStatusChange={handleStatusChange}
        />
        
        {/* Embed olmadığında göster */}
        {!isEmbedded && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-xs text-gray-500">
            <Button
              size="small" 
              type="dashed" 
              onClick={clearCache} 
              disabled={cacheInfo.activeCachedBrands === 0}
              className="text-xs"
            >
              Önbelleği Temizle
            </Button>
            <div className="text-right sm:text-left">
              Önbellekteki marka sayısı: {cacheInfo.activeCachedBrands}
              {cacheInfo.activeCachedBrands > 0 && 
                <span className="hidden sm:inline"> (10 dakika boyunca geçerli)</span>
              }
            </div>
          </div>
        )}
        
        {/* Tüm markalar token'ı için gösterilen başlık */}
        {isEmbedded && tokenData?.allBrands && (
          <div className="text-center p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <h2 className="text-lg sm:text-xl font-bold mb-2">Tüm Markalar Dashboard</h2>
            <p className="text-sm sm:text-base text-gray-700">Bu gösterge panelinde tüm markaların metrikleri gösterilmektedir.</p>
          </div>
        )}
      </div>
      
      {/* Üst sayfalama bileşeni */}
      {(!isFiltering || (isFiltering && filteredBrands.length > 0)) && (
        <div className="my-3 sm:my-4">
          {renderPagination}
        </div>
      )}
      
      {/* Metrik Seçici Popup */}
      <MetricSelector
        open={isMetricSelectorVisible}
        onCancel={() => setIsMetricSelectorVisible(false)}
        onSave={handleMetricsSave}
        allMetricsData={allMetricsData}
        monthlyMetricsData={{}}
        brands={allBrands}
        selectedMetrics={selectedMetrics}
        defaultMetricDescriptions={defaultMetricDescriptions}
      />
      
      {/* Yeni Özet Tablo Modal - Büyük ve bağımsız */}
      <NewSummaryTableModal
        open={showSummaryModal}
        onCancel={handleCloseSummaryModal}
        selectedMetrics={selectedMetrics}
        selectedMetricDescriptions={selectedMetricDescriptions}
        selectedMonth={selectedMonth}
        onMetricsChange={handleModalMetricsChange}
        filteredBrands={brandsForModal}
      />
      
      {pageContent}
    </div>
  );
};

export default AllBrandsMetrics; 